/**
 * MG AutoTech V4 — finite, component-scoped interaction layer.
 * No global mutation observers and no dependency on legacy theme versions.
 */
(function mgV4Boot() {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initialized = new WeakSet();
  const heroCleanups = new WeakMap();
  const tiltCleanups = new WeakMap();
  const diagnosticCleanups = new WeakMap();

  document.documentElement.classList.add('mg-v4-js');

  function once(element, setup) {
    if (!element || initialized.has(element)) return;
    initialized.add(element);
    setup(element);
  }

  function initReveals(scope) {
    const items = [...scope.querySelectorAll('[data-mgv4-reveal]')];
    const pending = items.filter((item) => !initialized.has(item));
    if (!pending.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      pending.forEach((item) => {
        initialized.add(item);
        item.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    pending.forEach((item) => {
      initialized.add(item);
      observer.observe(item);
    });
  }

  function initHero(hero) {
    once(hero, (root) => {
      const media = root.querySelector('[data-mgv4-hero-media]');
      const lab = root.querySelector('[data-mgv4-lab]');
      let heroVisible = !('IntersectionObserver' in window);
      let motionObserver = null;
      let resetPointerMotion = () => {};
      let destroyPointerMotion = () => {};

      const syncAmbientMotion = () => {
        root.classList.toggle('is-motion-active', heroVisible && !reduceMotion.matches && !document.hidden);
      };

      const handleHeroVisibility = () => {
        syncAmbientMotion();
        if (document.hidden) resetPointerMotion();
      };
      const handleHeroMotionPreference = () => {
        syncAmbientMotion();
        if (reduceMotion.matches) resetPointerMotion();
      };
      const handlePointerCapability = () => {
        if (!finePointer.matches) resetPointerMotion();
      };

      if (media) {
        let frame = 0;
        let bounds = null;
        let pointerActive = false;
        let targetX = 0;
        let targetY = 0;
        let currentX = 0;
        let currentY = 0;

        const cacheBounds = () => {
          if (!root.isConnected) {
            bounds = null;
            return;
          }
          const nextBounds = root.getBoundingClientRect();
          bounds = nextBounds.width > 0 && nextBounds.height > 0
            ? {
                left: nextBounds.left,
                top: nextBounds.top,
                width: nextBounds.width,
                height: nextBounds.height,
              }
            : null;
        };

        const clearFrame = () => {
          if (!frame) return;
          window.cancelAnimationFrame(frame);
          frame = 0;
        };

        const clearInlineTransforms = () => {
          media.style.removeProperty('transform');
          lab?.style.removeProperty('transform');
        };

        const render = () => {
          frame = 0;
          if (reduceMotion.matches || !finePointer.matches || document.hidden || !root.isConnected) {
            targetX = 0;
            targetY = 0;
            currentX = 0;
            currentY = 0;
            clearInlineTransforms();
            return;
          }

          currentX += (targetX - currentX) * 0.075;
          currentY += (targetY - currentY) * 0.075;
          media.style.transform = `scale(1.045) translate3d(${currentX}px, ${currentY}px, 0)`;
          if (lab) {
            lab.style.transform = `translate3d(${currentX * -0.14}px, ${currentY * -0.12}px, 0)`;
          }
          if (Math.abs(targetX - currentX) > 0.05 || Math.abs(targetY - currentY) > 0.05) {
            frame = window.requestAnimationFrame(render);
          }
        };

        const requestRender = () => {
          if (!frame) frame = window.requestAnimationFrame(render);
        };

        const handlePointerEnter = () => {
          if (reduceMotion.matches || !finePointer.matches || document.hidden) return;
          pointerActive = true;
          cacheBounds();
        };

        const handlePointerMove = (event) => {
          if (!pointerActive || !bounds || reduceMotion.matches || !finePointer.matches || document.hidden) return;
          targetX = ((event.clientX - bounds.left) / bounds.width - 0.5) * -12;
          targetY = ((event.clientY - bounds.top) / bounds.height - 0.5) * -8;
          requestRender();
        };

        const handlePointerLeave = () => {
          clearFrame();
          pointerActive = false;
          bounds = null;
          targetX = 0;
          targetY = 0;
          currentX = 0;
          currentY = 0;
          clearInlineTransforms();
        };

        const handleResize = () => {
          if (pointerActive && !reduceMotion.matches && finePointer.matches && !document.hidden) cacheBounds();
          else bounds = null;
        };

        resetPointerMotion = () => {
          clearFrame();
          pointerActive = false;
          bounds = null;
          targetX = 0;
          targetY = 0;
          currentX = 0;
          currentY = 0;
          clearInlineTransforms();
        };

        root.addEventListener('pointerenter', handlePointerEnter, { passive: true });
        root.addEventListener('pointermove', handlePointerMove, { passive: true });
        root.addEventListener('pointerleave', handlePointerLeave, { passive: true });
        root.addEventListener('pointercancel', handlePointerLeave, { passive: true });
        window.addEventListener('resize', handleResize, { passive: true });

        destroyPointerMotion = () => {
          root.removeEventListener('pointerenter', handlePointerEnter);
          root.removeEventListener('pointermove', handlePointerMove);
          root.removeEventListener('pointerleave', handlePointerLeave);
          root.removeEventListener('pointercancel', handlePointerLeave);
          window.removeEventListener('resize', handleResize);
          resetPointerMotion();
        };
      }

      if ('IntersectionObserver' in window) {
        motionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries.find((candidate) => candidate.target === root);
            if (!entry) return;
            heroVisible = entry.isIntersecting;
            syncAmbientMotion();
          },
          { rootMargin: '80px 0px', threshold: 0.01 }
        );
        motionObserver.observe(root);
      } else {
        syncAmbientMotion();
      }

      document.addEventListener('visibilitychange', handleHeroVisibility);
      reduceMotion.addEventListener?.('change', handleHeroMotionPreference);
      finePointer.addEventListener?.('change', handlePointerCapability);
      heroCleanups.set(root, () => {
        motionObserver?.disconnect();
        document.removeEventListener('visibilitychange', handleHeroVisibility);
        reduceMotion.removeEventListener?.('change', handleHeroMotionPreference);
        finePointer.removeEventListener?.('change', handlePointerCapability);
        destroyPointerMotion();
        root.classList.remove('is-motion-active');
        initialized.delete(root);
        heroCleanups.delete(root);
      });
    });
  }

  function initSearch(search) {
    once(search, (root) => {
      const modes = [...root.querySelectorAll('[data-mgv4-search-mode]')];
      const input = root.querySelector('[data-mgv4-search-input]');
      const context = root.querySelector('[data-mgv4-search-context]');
      if (!modes.length || !input) return;

      const activate = (button, focusInput) => {
        modes.forEach((mode) => {
          const selected = mode === button;
          mode.setAttribute('aria-pressed', String(selected));
        });
        input.placeholder = button.dataset.placeholder || input.placeholder;
        input.setAttribute('aria-label', button.dataset.label || button.textContent.trim());
        if (context) context.value = button.dataset.mode || 'vehicle';
        if (focusInput) input.focus({ preventScroll: true });
      };

      modes.forEach((button, index) => {
        button.addEventListener('click', () => activate(button, true));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + modes.length) % modes.length;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % modes.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = modes.length - 1;
          activate(modes[nextIndex], false);
          modes[nextIndex].focus();
        });
      });

      root.addEventListener('submit', () => {
        if (context?.value !== 'vehicle') return;
        const rawQuery = input.value.trim();
        if (!rawQuery || /\b(?:AND|OR|NOT)\b/i.test(rawQuery)) return;

        const terms = rawQuery
          .split(/\s+/)
          .map((term) => term.replace(/[():"\\]/g, ''))
          .filter((term) => term.length > 1)
          .slice(0, 6);

        /* Shopify combines whitespace terms with AND. Vehicle descriptions rarely contain
           every chassis/model token, so use the supported query grammar to return the most
           relevant match for any supplied vehicle identifier instead of an empty result. */
        if (terms.length > 1) input.value = `(${terms.join(' OR ')})`;
      });
    });
  }

  function initTilt(card) {
    once(card, (element) => {
      let frame = 0;
      let bounds = null;
      let pointerActive = false;
      let x = 0;
      let y = 0;

      const cacheBounds = () => {
        if (!element.isConnected) {
          bounds = null;
          return;
        }
        const nextBounds = element.getBoundingClientRect();
        bounds = nextBounds.width > 0 && nextBounds.height > 0
          ? {
              left: nextBounds.left,
              top: nextBounds.top,
              width: nextBounds.width,
              height: nextBounds.height,
            }
          : null;
      };

      const clearFrame = () => {
        if (!frame) return;
        window.cancelAnimationFrame(frame);
        frame = 0;
      };

      const clearInlineTilt = () => {
        element.style.removeProperty('--mgv4-tilt-x');
        element.style.removeProperty('--mgv4-tilt-y');
      };

      const render = () => {
        frame = 0;
        if (reduceMotion.matches || !finePointer.matches || document.hidden || !element.isConnected) {
          x = 0;
          y = 0;
          clearInlineTilt();
          return;
        }
        element.style.setProperty('--mgv4-tilt-x', `${y.toFixed(2)}deg`);
        element.style.setProperty('--mgv4-tilt-y', `${x.toFixed(2)}deg`);
      };

      const requestRender = () => {
        if (!frame) frame = window.requestAnimationFrame(render);
      };

      const handlePointerEnter = () => {
        if (reduceMotion.matches || !finePointer.matches || document.hidden) return;
        pointerActive = true;
        cacheBounds();
      };

      const handlePointerMove = (event) => {
        if (!pointerActive || !bounds || reduceMotion.matches || !finePointer.matches || document.hidden) return;
        x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 3.6;
        y = ((event.clientY - bounds.top) / bounds.height - 0.5) * -3.2;
        requestRender();
      };

      const handlePointerLeave = () => {
        pointerActive = false;
        bounds = null;
        x = 0;
        y = 0;
        requestRender();
      };

      const handleResize = () => {
        if (pointerActive && !reduceMotion.matches && finePointer.matches && !document.hidden) cacheBounds();
        else bounds = null;
      };

      const resetTilt = () => {
        clearFrame();
        pointerActive = false;
        bounds = null;
        x = 0;
        y = 0;
        clearInlineTilt();
      };

      const handleMotionPreference = () => {
        if (reduceMotion.matches) resetTilt();
      };

      const handlePointerCapability = () => {
        if (!finePointer.matches) resetTilt();
      };

      const handleVisibility = () => {
        if (document.hidden) resetTilt();
      };

      element.addEventListener('pointerenter', handlePointerEnter, { passive: true });
      element.addEventListener('pointermove', handlePointerMove, { passive: true });
      element.addEventListener('pointerleave', handlePointerLeave, { passive: true });
      element.addEventListener('pointercancel', handlePointerLeave, { passive: true });
      window.addEventListener('resize', handleResize, { passive: true });
      document.addEventListener('visibilitychange', handleVisibility);
      reduceMotion.addEventListener?.('change', handleMotionPreference);
      finePointer.addEventListener?.('change', handlePointerCapability);

      tiltCleanups.set(element, () => {
        element.removeEventListener('pointerenter', handlePointerEnter);
        element.removeEventListener('pointermove', handlePointerMove);
        element.removeEventListener('pointerleave', handlePointerLeave);
        element.removeEventListener('pointercancel', handlePointerLeave);
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('visibilitychange', handleVisibility);
        reduceMotion.removeEventListener?.('change', handleMotionPreference);
        finePointer.removeEventListener?.('change', handlePointerCapability);
        resetTilt();
        initialized.delete(element);
        tiltCleanups.delete(element);
      });
    });
  }

  function initProcess(process) {
    once(process, (root) => {
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        root.style.setProperty('--mgv4-progress', '1');
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          root.style.setProperty('--mgv4-progress', '1');
          observer.disconnect();
        },
        { threshold: 0.25 }
      );
      observer.observe(root);
    });
  }

  function initDiagnosticReadout(readout) {
    once(readout, (element) => {
      const codes = (element.dataset.codes || '')
        .split('|')
        .map((code) => code.trim())
        .filter(Boolean);
      if (codes.length < 2 || reduceMotion.matches) return;

      let index = 0;
      let intervalId = 0;
      let textTimeoutId = 0;
      let activeAnimation = null;
      let visible = !('IntersectionObserver' in window);
      let observer = null;

      const stop = () => {
        if (intervalId) window.clearInterval(intervalId);
        if (textTimeoutId) window.clearTimeout(textTimeoutId);
        activeAnimation?.cancel();
        intervalId = 0;
        textTimeoutId = 0;
        activeAnimation = null;
      };

      const tick = () => {
        if (!element.isConnected) {
          cleanup();
          return;
        }
        if (!visible || document.hidden || reduceMotion.matches) return;

        index = (index + 1) % codes.length;
        if (typeof element.animate === 'function') {
          activeAnimation = element.animate(
            [
              { opacity: 1, transform: 'translateY(0)' },
              { opacity: 0, transform: 'translateY(-4px)', offset: 0.45 },
              { opacity: 0, transform: 'translateY(4px)', offset: 0.55 },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' }
          );
        }
        textTimeoutId = window.setTimeout(() => {
          element.textContent = codes[index];
          textTimeoutId = 0;
        }, 200);
      };

      const start = () => {
        if (intervalId || !visible || document.hidden || reduceMotion.matches) return;
        intervalId = window.setInterval(tick, 3600);
      };

      const handleVisibility = () => {
        if (document.hidden) stop();
        else start();
      };

      const handleMotionPreference = () => {
        if (reduceMotion.matches) stop();
        else start();
      };

      const cleanup = () => {
        stop();
        observer?.disconnect();
        document.removeEventListener('visibilitychange', handleVisibility);
        reduceMotion.removeEventListener?.('change', handleMotionPreference);
        diagnosticCleanups.delete(element);
      };

      document.addEventListener('visibilitychange', handleVisibility);
      reduceMotion.addEventListener?.('change', handleMotionPreference);

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(
          (entries) => {
            const entry = entries.find((candidate) => candidate.target === element);
            if (!entry) return;
            visible = entry.isIntersecting;
            if (visible) start();
            else stop();
          },
          { rootMargin: '120px 0px', threshold: 0.01 }
        );
        observer.observe(element);
      } else {
        start();
      }

      diagnosticCleanups.set(element, cleanup);
    });
  }

  function initLocaleRouting() {
    const node = document.getElementById('MgV4LocalizationConfig');
    if (!node) return;

    let config;
    try {
      config = JSON.parse(node.textContent);
    } catch (error) {
      return;
    }

    document.addEventListener('change', (event) => {
      const field = event.target;
      if (!(field instanceof HTMLSelectElement || field instanceof HTMLInputElement)) return;
      if (field.name !== 'locale_code' && field.name !== 'language_code') return;
      try {
        window.localStorage.setItem('mgv4-locale-manual', '1');
      } catch (error) {
        // Storage can be unavailable in privacy modes; native localization still works.
      }
    });

    if (config.designMode || !Array.isArray(config.languages) || config.languages.length < 2) return;

    /* Search crawlers should receive stable locale URLs and Shopify hreflang signals,
       never a client-side language POST. */
    if (/(?:bot|crawler|spider|slurp|bingpreview|facebookexternalhit|linkedinbot|whatsapp)/i.test(navigator.userAgent)) {
      return;
    }

    const current = String(config.current || '').toLowerCase().split('-')[0];
    const pathname = window.location.pathname.replace(/\/{2,}/g, '/');
    const explicitLocale = config.languages
      .filter((language) => language.rootUrl && language.rootUrl !== '/')
      .sort((a, b) => String(b.rootUrl).length - String(a.rootUrl).length)
      .find((language) => {
        const root = String(language.rootUrl).replace(/\/$/, '');
        return pathname === root || pathname.startsWith(`${root}/`);
      });

    /* A locale-prefixed URL is an explicit visitor choice. Do not replace /de
       with the browser's English preference (or vice versa). */
    if (explicitLocale && String(explicitLocale.isoCode || '').toLowerCase().split('-')[0] === current) {
      try {
        window.sessionStorage.setItem('mgv4-locale-resolved', '1');
      } catch (error) {
        // The explicit URL is enough to resolve the locale without storage.
      }
      return;
    }

    let manualLocale = false;
    let localeResolved = false;
    try {
      manualLocale = window.localStorage.getItem('mgv4-locale-manual') === '1';
      localeResolved = window.sessionStorage.getItem('mgv4-locale-resolved') === '1';
    } catch (error) {
      // Continue once without persistence; the submitted Shopify locale prevents a loop.
    }
    if (manualLocale || localeResolved) return;

    const preferred = (navigator.languages || [navigator.language])
      .map((language) => String(language || '').toLowerCase().split('-')[0])
      .find((language) => language === 'de' || language === 'en');
    if (!preferred) return;

    const target = config.languages.find(
      (language) => String(language.isoCode || '').toLowerCase().split('-')[0] === preferred
    );
    if (!target || current === preferred) {
      try {
        window.sessionStorage.setItem('mgv4-locale-resolved', '1');
      } catch (error) {
        // No action required.
      }
      return;
    }

    const form = document.getElementById('MgV4AutoLocaleForm');
    const languageInput = form?.querySelector('[data-mgv4-auto-language]');
    if (!(form instanceof HTMLFormElement) || !(languageInput instanceof HTMLInputElement)) return;

    try {
      window.sessionStorage.setItem('mgv4-locale-resolved', '1');
    } catch (error) {
      // Continue: the native Shopify localization POST stores the chosen locale.
    }

    languageInput.value = target.isoCode;
    form.requestSubmit();
  }

  function init(scope = document) {
    initReveals(scope);
    scope.querySelectorAll('[data-mgv4-hero]').forEach(initHero);
    scope.querySelectorAll('[data-mgv4-search]').forEach(initSearch);
    scope.querySelectorAll('[data-mgv4-tilt]').forEach(initTilt);
    scope.querySelectorAll('[data-mgv4-process]').forEach(initProcess);
    scope.querySelectorAll('[data-mgv4-code]').forEach(initDiagnosticReadout);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initLocaleRouting();
        init(document);
      },
      { once: true }
    );
  } else {
    initLocaleRouting();
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-mgv4-hero]').forEach((element) => {
      heroCleanups.get(element)?.();
    });
    event.target.querySelectorAll('[data-mgv4-code]').forEach((element) => {
      diagnosticCleanups.get(element)?.();
    });
    event.target.querySelectorAll('[data-mgv4-tilt]').forEach((element) => {
      tiltCleanups.get(element)?.();
    });
  });
})();
